import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

const STAT_CARD_BASE_CLASS = 'rounded-xl border border-outline bg-surface p-4 flex flex-col gap-2';

/** Direction of the delta compared to a baseline. */
export type StatDeltaDirection = 'up' | 'down' | 'neutral';

/**
 * Dashboard KPI card: label + value + optional delta + optional icon and footer slots.
 *
 * Uses semantic surface tokens and adapts to dark mode. Delta color is not the sole
 * indicator of direction — a matching arrow icon is always shown.
 *
 * @example
 * <app-stat-card label="Goals on track" value="18" delta="+12%" deltaDirection="up">
 *   <app-icon icon name="trending-up" />
 *   <span footer>vs. last month</span>
 * </app-stat-card>
 *
 * @feature shared
 */
@Component({
  selector: 'app-stat-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  template: `
    <div [class]="containerClass()">
      <div class="flex items-start justify-between">
        <span class="text-sm font-semibold text-muted">{{ label() }}</span>
        <ng-content select="[icon]" />
      </div>

      <div class="flex items-baseline gap-2">
        <span class="text-3xl font-heading font-bold text-body">{{ value() }}</span>
        @if (delta()) {
          <span class="inline-flex items-center gap-0.5 text-sm font-semibold" [class]="deltaClass()">
            <app-icon [name]="deltaIcon()" size="xxs" />
            {{ delta() }}
          </span>
        }
      </div>

      <div class="text-sm text-muted">
        <ng-content select="[footer]" />
      </div>
    </div>
  `,
})
export class StatCardComponent {
  /** Stat label. */
  readonly label = input.required<string>();

  /** Display value. String so callers control formatting. */
  readonly value = input.required<string | number>();

  /** Whether the card renders its default elevation shadow. @default true */
  readonly shadow = input<boolean>(true);

  /** Resolved Tailwind class list for the card container. */
  protected readonly containerClass = computed(() =>
    this.shadow() ? `${STAT_CARD_BASE_CLASS} shadow-[var(--theme-shadow)]` : STAT_CARD_BASE_CLASS,
  );

  /** Optional delta label, e.g. "+12%". */
  readonly delta = input<string | undefined>(undefined);

  /** Direction of the delta. Drives both the icon and color. @default 'neutral' */
  readonly deltaDirection = input<StatDeltaDirection>('neutral');

  /** Resolved Tailwind text class for the delta. */
  readonly deltaClass = computed(() => {
    switch (this.deltaDirection()) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-danger';
      case 'neutral':
      default:
        return 'text-muted';
    }
  });

  /** Resolved icon for the delta direction. */
  readonly deltaIcon = computed(() => {
    switch (this.deltaDirection()) {
      case 'up':
        return 'arrow-up';
      case 'down':
        return 'arrow-down';
      case 'neutral':
      default:
        return 'remove';
    }
  });
}
